// Lightweight i18n for Farveladeland
// Danish default at "/", English at "/en/..."

import { createContext, useContext } from "react";

export type Locale = "da" | "en";
export const LOCALES: Locale[] = ["da", "en"];
export const DEFAULT_LOCALE: Locale = "da";

export const SITE_URL = "https://farveladeland.com";

export const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

// Detect locale from any pathname
export function localeFromPath(pathname: string): Locale {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  return "da";
}

// Build a locale-prefixed href, e.g. localeHref("en", "/about") => "/en/about"
export function localeHref(locale: Locale, path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === "da") return clean;
  if (clean === "/") return "/en";
  return `/en${clean}`;
}

// Strip the /en prefix from a path
export function stripLocale(pathname: string): string {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname;
}

// ----- Dictionary -----
// Tone: warm, konkret, hverdagsdansk. Inspired by the original farveladeland.com copy.
// Avoid 1:1 translations and "poetic" phrases. Keep it short and butiks-nært.

export const dict = {
  da: {
    // Top utility
    "topbar.dispatch": "Vi sender hver mandag",
    "topbar.flea": "Nye loppefund hver dag",
    "topbar.book": "Bestil en fremvisning",
    "topbar.sale": "27% på Sommerudsalg — trækkes automatisk fra i kassen",


    // Header
    "header.estMurano": "Vintage Murano",
    "header.basket": "Kurv",
    "header.about": "Om os",
    "header.menuOpen": "Åbn menu",
    "header.menuClose": "Luk menu",
    "header.langSwitch": "Skift sprog",
    "header.newBadge": "Ny",
    "header.japanCollection": "Fund fra Japan",

    // Mega menu groups
    "nav.murano": "Murano",
    "nav.lamps": "Lamper",
    "nav.tableware": "Borddækning",
    "nav.glass": "Glas",
    "nav.interior": "Interiør",
    "nav.sale": "Udsalg",
    "nav.browseAll": "Se alle vores {label} — håndplukket fra loppemarkeder, dødsboer og rejser i Italien.",

    // Footer
    "footer.tagline.line1": "Secondhand, genbrug",
    "footer.tagline.line2": "& vintage Murano.",
    "footer.about":
      "Farveladeland er en lille, dansk webshop for vintage Murano-lamper og andre farverige fund fra loppemarkeder rundt omkring i verden.",
    "footer.wander": "Genveje",
    "footer.editMurano": "Murano",
    "footer.allCollections": "Alle varer",
    "footer.showroom": "Showroom",
    "footer.story": "Om os",
    "footer.bookViewing": "Bestil fremvisning",
    "footer.leksikon": "Murano-leksikon",
    "footer.visit": "Besøg os",
    "footer.address": "Munkensvej 4, 2.tv\n2000 Frederiksberg\nbutik@farveladeland.com",
    "footer.instagram": "Instagram",
    "footer.newsletter": "Nyhedsbrev",
    "footer.copy": "Farveladeland · Sender til hele verden",
    "footer.handblown": "Vintage Murano · Kurateret i København",

    // Home
    "home.eyebrow": "Secondhand · Vintage · Murano",
    "home.hero.l1": "Secondhand,",
    "home.hero.l2": "genbrug",
    "home.hero.l3": "& vintage Murano.",
    "home.hero.body":
      "Hos Farveladeland finder du vintage Murano-lamper og farverige fund fra loppemarkeder, dødsboer og rejser i Europa. Hvert stykke er unikt — og når det er solgt, er det solgt.",
    "home.hero.cta1": "Se Murano-udvalget",
    "home.hero.cta2": "Bestil en fremvisning",
    "home.hero.strip1": "Vi sender hver mandag",
    "home.hero.strip2": "Sender til hele verden",
    "home.hero.strip3": "Showroom på Frederiksberg",

    "home.outlet.eyebrow": "Murano outlet",
    "home.outlet.titleA": "Spar",
    "home.outlet.titleB": "15–45 % på udvalgte stykker.",
    "home.outlet.body":
      "Et roterende udvalg af Murano-lamper med små skønhedsfejl eller bare klar til at finde et nyt hjem.",
    "home.outlet.cta": "Se udvalget",
    "home.outlet.imgAlt": "Murano outlet",

    "home.pillars.showroom.title": "Fremvisning",
    "home.pillars.showroom.sub": "Showroom",
    "home.pillars.showroom.text": "Du kan bestille tid til at se vores showroom på Frederiksberg.",
    "home.pillars.showroom.cta": "Book en tid",
    "home.pillars.all.title": "Alle varer",
    "home.pillars.all.sub": "Webshop",
    "home.pillars.all.text": "Se hele udvalget i webshoppen.",
    "home.pillars.all.cta": "Find dem her",
    "home.pillars.wedding.title": "Bryllupsservice",
    "home.pillars.wedding.sub": "Lej til festen",
    "home.pillars.wedding.text": "Se hele vores opsætning til brylluppet her.",
    "home.pillars.wedding.cta": "Tryk her",

    "home.murano.eyebrow": "Murano-kollektionen",
    "home.murano.titleA": "Tre slags",
    "home.murano.titleB": "Murano-lamper.",
    "home.murano.seeAll": "Se alle",
    "home.murano.shopColl": "Se kollektionen",
    "home.murano.ceiling": "Murano loftlamper",
    "home.murano.mushroom": "Murano mushroom lamper",
    "home.murano.wall": "Murano væglamper",

    "home.justin.eyebrow": "Netop landet",
    "home.justin.title": "Lige kommet ind.",
    "home.justin.seeAll": "Se alle",
    "home.justin.loading": "Henter varer…",

    // Hero featured chip
    "home.hero.featured": "Ugens lampe",

    // Top 10 — most-viewed marquee
    "home.colorland.eyebrow": "Top 10",
    "home.colorland.titleA": "Mest sete",
    "home.colorland.titleB": "denne uge.",
    "home.colorland.body":
      "Det, vores gæster klikker mest på lige nu — opdateres dagligt. Kig ind, før de ryger.",
    "home.colorland.empty": "Vi indlæser ugens favoritter…",

    // Shop by colour — the homepage colour grid
    "home.colorshop.eyebrow": "Shop efter farve",
    "home.colorshop.titleA": "Find din",
    "home.colorshop.titleB": "yndlingsfarve.",
    "home.colorshop.body":
      "Vælg en farve og se alt mundblæst Murano-glas, lamper og fund i netop dén tone.",

    // Colours landing page (/farver)
    "colors.eyebrow": "Shop efter farve",
    "colors.titleA": "Otte farver,",
    "colors.titleB": "ét land.",
    "colors.body":
      "Vælg en tone og find lamper, glas og fund i netop dén farve — kurateret fra vores hylder i Frederiksberg.",
    "colors.count": "{n} farver",

    // Colour detail page (/farver/$slug)
    "color.crumbHome": "Forsiden",
    "color.crumbColors": "Farver",
    "color.empty": "Vi har ikke noget i denne farve lige nu — kig forbi om et par dage.",
    "color.seeAll": "Se alle Murano-lamper",
    "color.allColors": "Alle farver",
    "color.count": "{n} fund",

    // Sizes (/storrelse, /storrelse/$range)
    "sizes.eyebrow": "Shop efter størrelse",
    "sizes.titleA": "Lamper i",
    "sizes.titleB": "din størrelse.",
    "sizes.body":
      "Find lamper, pendler og lysekroner ud fra diameteren — fra de små bordlamper på 18 cm til de store pendler på 50 cm og opefter. Vi læser målene direkte fra produktbeskrivelsen.",
    "sizes.count": "{n} størrelser",
    "sizes.unit": "Diameter, Ø",
    "size.crumbHome": "Forsiden",
    "size.crumbSizes": "Størrelse",
    "size.empty": "Vi har ikke nogen lamper i denne størrelse lige nu — kig forbi om et par dage.",
    "size.seeAll": "Se alle Murano-lamper",
    "size.allSizes": "Alle størrelser",
    "size.count": "{n} lamper",
    "size.range": "Diameter Ø",

    // Editorial scenes — captions under each Murano collection
    "home.scenes.ceiling":
      "Pendler, plafonder og lysekroner — det første du ser, når du træder ind ad døren.",
    "home.scenes.mushroom":
      "Den lille runde lampe på sengebordet, der gør et soveværelse til et soveværelse.",
    "home.scenes.wall":
      "Et roligt lys i gangen, ved sengen, eller dér hvor man læser.",

    // Curator's note
    "home.curator.eyebrow": "Ugens fund",
    "home.curator.titleA": "Hånd-",
    "home.curator.titleB": "plukket.",
    "home.curator.quote":
      "Den her kom hjem fra et loppemarked uden for Bologna. Den lyserøde nuance er sjælden — det meste '70er-Murano hælder mod ravgul. Den blev lige sat på hylden.",
    "home.curator.signoff": "— Hilsen fra butikken",
    "home.curator.thisWeek": "Denne uges udvalgte",
    "home.curator.cta": "Se varen",


    // Collections index
    "collections.eyebrow": "Alle varer",
    "collections.titleA": "Hele",
    "collections.titleB": "udvalget.",
    "collections.body":
      "{n} kollektioner med vintage Murano, glas, porcelæn og andre farverige fund fra loppemarkeder i Europa.",
    "collections.count": "{n} kollektioner",
    "collections.browseAll": "Se alle produkter",

    // Collection page
    "collection.back": "← Alle kollektioner",
    "collection.eyebrow": "Farveladeland",
    "collection.empty": "Der er ingen varer i denne kollektion lige nu.",
    "collection.browseOther": "Se andre kollektioner",
    "collection.piece_one": "{n} vare",
    "collection.piece_other": "{n} varer",
    "collection.notFound.eyebrow": "Tom hylde",
    "collection.notFound.title": "Vi kunne ikke finde kollektionen",
    "collection.notFound.body": "Den er måske flyttet eller omdøbt.",
    "collection.notFound.cta": "Tilbage til alle kollektioner",
    "collection.error.eyebrow": "Hov",
    "collection.error.title": "Vi kunne ikke hente kollektionen",
    "collection.error.cta": "Prøv igen",
    "collection.about": "Om kollektionen",
    "collection.highlights": "Godt at vide",
    "collection.fallbackIntro":
      "Et lille, kurateret udvalg af vintage og secondhand fra Farveladeland. Vi sender hver mandag fra Frederiksberg.",

    // Product page
    "product.back": "← Tilbage til shoppen",
    "product.eyebrow": "Vintage · Farveladeland",
    "product.variant": "Variant",
    "product.add": "Læg i kurv",
    "product.sold": "Udsolgt",
    "product.about": "Beskrivelse",
    "product.dispatch": "Forsendelse",
    "product.dispatchVal": "Hver mandag",
    "product.shipping": "Levering",
    "product.shippingVal": "Hele verden",
    "product.related.eyebrow": "Netop landet",
    "product.related.title": "Mere fra shoppen",
    "product.notFound.eyebrow": "Den fandt et nyt hjem",
    "product.notFound.title": "Varen er ikke længere på lager",
    "product.notFound.cta": "Se andre varer",
    "product.error.title": "Vi kunne ikke hente varen",
    "product.error.cta": "Prøv igen",

    // Murano page
    "murano.eyebrow": "Murano",
    "murano.titleA": "Vintage Murano",
    "murano.titleB": "fra Italien,",
    "murano.titleC": "kurateret i København.",
    "murano.body":
      "Vi rejser jævnligt til Italien og hjembringer Murano-lamper og glas, vi selv ville sætte i vores eget hjem. Alt er secondhand, alt er unikt — og vi pakker hver enkelt med omhu, før det sendes ud i verden.",
    "murano.shopCta": "Se kollektionen",
    "murano.editions.01.title": "Murano loftlamper",
    "murano.editions.01.italian": "Lampadari",
    "murano.editions.01.text":
      "Hos Farveladeland har vi altid mange flotte Murano loftlamper på vores webshop — pendler, plafonder og lysekroner i alt fra hvide swirls til pastelbånd.",
    "murano.editions.01.range": "Fra 4.500 kr.",
    "murano.editions.02.title": "Murano mushroom lamper",
    "murano.editions.02.italian": "Funghi",
    "murano.editions.02.text":
      "Hos Farveladeland har vi altid mange flotte Murano mushroom lamper på hjemmesiden — i lyserød, hvid, smørgul og lyseblå.",
    "murano.editions.02.range": "Fra 1.800 kr.",
    "murano.editions.03.title": "Murano væglamper",
    "murano.editions.03.italian": "Appliques",
    "murano.editions.03.text":
      "Hos Farveladeland har vi altid mange flotte Murano væglamper på vores webshop — perfekte til entréen, gangen eller sengen.",
    "murano.editions.03.range": "Fra 2.400 kr.",
    "murano.fact.1.k": "1291",
    "murano.fact.1.v": "Året glasmagerne blev henvist til Murano, så Venedig ikke skulle brænde ned.",
    "murano.fact.2.k": "1.450°C",
    "murano.fact.2.v": "Temperaturen, hvor farve, bladguld og luft smelter sammen til ét.",
    "murano.fact.3.k": "Én af én",
    "murano.fact.3.v": "Hvert stykke er unikt. Når det er solgt, er det solgt.",

    // Murano matcher
    "match.eyebrow": "Find din Murano",
    "match.cta": "Hjælp mig med at vælge",
    "match.ctaSub": "5 spørgsmål · ca. 30 sekunder",

    // Showroom
    "showroom.eyebrow": "Showroom · Frederiksberg",
    "showroom.titleA": "Kig forbi",
    "showroom.titleB": "showroomet.",
    "showroom.appt.eyebrow": "Efter aftale",
    "showroom.appt.title": "Se varerne i virkeligheden.",
    "showroom.appt.body1":
      "Vores showroom på Frederiksberg er lille og hyggeligt. Vi tager imod én ad gangen, så du i ro og mag kan tænde lamperne, løfte porcelænet og se det hele i naturligt lys.",
    "showroom.appt.body2":
      "Skriv til os på Instagram eller mail, og vi finder et tidspunkt sammen.",
    "showroom.appt.cta": "Bestil en fremvisning",
    "showroom.info.address.k": "Adresse",
    "showroom.info.address.v": "Munkensvej 4, 2.tv\n2000 Frederiksberg",
    "showroom.info.hours.k": "Åbent",
    "showroom.info.hours.v": "Efter aftale\nOftest hverdage efter kl. 17",
    "showroom.info.dispatch.k": "Forsendelse",
    "showroom.info.dispatch.v": "Hver mandag\nSender til hele verden",

    // About
    "about.eyebrow": "Om os",
    "about.titleA": "Vi samler",
    "about.titleB": "det farverige.",
    "about.intro":
      "Farveladeland — eller \"farvernes land\" — er en lille webshop for vintage Murano og andre farverige fund fra loppemarkeder rundt omkring i verden.",
    "about.p1":
      "Vi rejser jævnligt, oftest til små italienske byer, og kommer hjem med biler fyldt med omhyggeligt indpakkede lamper, glas og porcelæn.",
    "about.p2":
      "Hjemme i København rengør vi hvert stykke, fotograferer det i naturligt lys og skriver alt det ned, vi ved om det. Så lægger vi det i shoppen og venter på, at det finder det rigtige hjem.",
    "about.p3":
      "Intet er masseproduceret. Intet er ens. Når et stykke er solgt, er det solgt — og det er sådan, vi bedst kan lide det.",
    "about.believe.eyebrow": "Sådan gør vi",
    "about.believe.titleA": "Langsom handel,",
    "about.believe.titleB": "lang glæde.",
    "about.believe.l1": "Hvert stykke fotograferes i ærligt dagslys, så du ser præcis hvad du får — fejl og det hele.",
    "about.believe.l2": "Vi pakker grundigt. Murano rejser i bobleplast, avis og ekstra pap.",
    "about.believe.l3": "Passer det ikke alligevel hjemme hos dig, kan du returnere inden for 14 dage.",

    // Visit
    "visit.eyebrow": "Bestil en fremvisning",
    "visit.titleA": "Kig forbi",
    "visit.titleB": "showroomet.",
    "visit.body":
      "Skriv hvornår du gerne vil komme forbi, og hvad du leder efter. Vi vender tilbage hurtigst muligt med tid og adresse.",
    "visit.info.hours.k": "Åbent",
    "visit.info.hours.v": "Efter aftale",
    "visit.info.email.k": "Mail",
    "visit.info.email.v": "butik@farveladeland.com",
    "visit.info.ig.k": "Instagram",
    "visit.info.ig.v": "@farveladeland",
    "visit.info.dispatch.k": "Forsendelse",
    "visit.info.dispatch.v": "Hver mandag · hele verden",
    "visit.form.name": "Navn",
    "visit.form.email": "Mail",
    "visit.form.date": "Foretrukket dato",
    "visit.form.message": "Hvad leder du efter?",
    "visit.form.placeholder":
      "En loftlampe til spisestuen, noget i petroleumsblå, en lysekrone under 60 cm…",
    "visit.form.submit": "Send",
    "visit.thanks.eyebrow": "Tak",
    "visit.thanks.title": "Vi har modtaget din besked.",
    "visit.thanks.body": "Vi vender tilbage hurtigst muligt med tid og adresse.",

    // Cart
    "cart.title": "Din kurv",
    "cart.empty": "Din kurv er tom.",
    "cart.items_one": "{n} vare i kurven.",
    "cart.items_other": "{n} varer i kurven.",
    "cart.emptyHelp": "Endnu intet i kurven — find et fund i shoppen.",
    "cart.browse": "Til shoppen",
    "cart.subtotal": "Subtotal",
    "cart.subtotalNote": "Inkl. moms. Fragt beregnes ved kassen.",
    "cart.checkout": "Til kassen",
    "cart.decrease": "Færre",
    "cart.increase": "Flere",
    "cart.remove": "Fjern",
    "cart.added": "Lagt i kurven",
    "cart.viewCart": "Se kurv",

    // Header / nav extras
    "header.shop": "Shop",
    "header.murano": "Murano",
    "header.colors": "Farver",
    "header.sizes": "Størrelse",
    "header.showroom": "Showroom",
    "header.match": "Hjælp mig med at vælge",
    "header.blog": "Guides",
    "header.allGoods": "Alle varer",
    "header.allProducts": "Alle produkter",
    "home.allProducts.cta": "Se alle produkter",
    "home.allProducts.sub": "Browse hele butikken på tværs af kategorier.",
    "header.shopByColor": "Shop efter farve",
    "header.shopBySize": "Shop efter størrelse",
    "header.allColors": "Se alle farver",
    "header.allSizes": "Se alle størrelser",
    "header.search": "Søg",
    "header.searchPlaceholder": "Søg efter farve, form eller størrelse…",
    "header.searchHint": "Tryk ⌘K for at søge",
    "search.products": "Produkter",
    "search.categories": "Kategorier",
    "search.sizes": "Størrelser",
    "search.colors": "Farver",
    "search.recent": "Seneste søgninger",
    "search.popular": "Populære søgninger",
    "search.empty": "Ingen resultater for {q}",
    "search.emptyHint": "Prøv en farve som »lyserød« eller en form som »mushroom«.",
    "search.viewAll": "Se alle {n} resultater",
    "search.title": "Søgeresultater",
    "search.resultsFor": "Resultater for »{q}«",
    "search.resultsCount": "{n} produkter fundet",
    "search.noResults": "Ingen resultater",
    "search.tryAnother": "Prøv en anden søgning, eller browse efter farve eller størrelse i menuen.",
    "search.close": "Luk søgning",
    "search.clear": "Ryd",

    // Trust strip
    "trust.dispatch.k": "Sender hver mandag",
    "trust.dispatch.v": "Pakket i avis, bobleplast og ekstra pap.",
    "trust.shipping.k": "Hele verden",
    "trust.shipping.v": "Vi sender til alle EU-lande og resten af verden.",
    "trust.returns.k": "14 dages retur",
    "trust.returns.v": "Passer det ikke hjemme hos dig, tager vi det gerne retur.",
    "trust.unique.k": "Én af én",
    "trust.unique.v": "Hvert stykke er unikt — når det er solgt, er det solgt.",

    // Founder note
    "founder.eyebrow": "Hilsen fra butikken",
    "founder.title": "Vi pakker selv hver eneste pakke.",
    "founder.body":
      "Farveladeland er en lille butik. Vi rejser selv ud, fotograferer selv hvert stykke i dagslys og pakker hver pakke med avis og bobleplast. Skriv endelig, hvis du er i tvivl om en lampe — vi svarer som regel samme dag.",
    "founder.cta": "Skriv til os",

    // PDP extras
    "product.dimensions": "Detaljer",
    "product.zoom": "Klik for at zoome",
    "product.zoomClose": "Luk zoom",
    "product.imageOf": "Billede {n} af {total}",
    "product.prevImage": "Forrige billede",
    "product.nextImage": "Næste billede",
    "product.stock.in": "På lager — kun ét stykke",
    "product.stock.out": "Udsolgt",
    "product.shareTitle": "Del",

    // 404
    "notFound.eyebrow": "Tom hylde",
    "notFound.body":
      "Siden findes ikke. Den er måske solgt, flyttet — eller har aldrig været her.",
    "notFound.cta": "Tilbage til forsiden",

    // Badges
    "badge.new": "Lige landet",
    "badge.sold": "Udsolgt",
    "badge.sale": "Tilbud",
    "price.was": "Førpris {price}",

    // Wishlist
    "wishlist.add": "Gem til ønskelisten",
    "wishlist.remove": "Fjern fra ønskelisten",
    "wishlist.title": "Min ønskeliste",
    "wishlist.eyebrow": "Gemt til senere",
    "wishlist.empty": "Du har ikke gemt noget endnu.",
    "wishlist.emptyHelp":
      "Tryk på hjertet på et stykke, du gerne vil holde øje med — så finder du det her igen.",
    "wishlist.browse": "Udforsk shoppen",
    "wishlist.count_one": "{n} gemt stykke",
    "wishlist.count_other": "{n} gemte stykker",
    "wishlist.clear": "Ryd ønskelisten",
    "wishlist.unavailable":
      "Den her er desværre ikke længere på lager — andre fund venter dog.",
    "wishlist.nav": "Ønskeliste",
    "wishlist.toastAdded": "Gemt på din ønskeliste",
    "wishlist.toastRemoved": "Fjernet fra ønskelisten",
    "wishlist.toastAction": "Se ønskelisten",
    "price.from": "Fra {price}",

    // Recently viewed
    "recent.title": "Senest set",
    "recent.eyebrow": "Du kiggede på",
    "recent.clear": "Ryd",

    // Sort & filter (collection pages)
    "filter.title": "Filtrér & sortér",
    "filter.sort": "Sortér",
    "filter.sort.featured": "Anbefalede",
    "filter.sort.newest": "Nyeste først",
    "filter.sort.priceAsc": "Pris: lav til høj",
    "filter.sort.priceDesc": "Pris: høj til lav",
    "filter.sort.titleAsc": "Titel A–Å",
    "filter.availability": "Tilgængelighed",
    "filter.inStock": "Kun på lager",
    "filter.price": "Pris",
    "filter.priceAny": "Alle priser",
    "filter.priceUnder": "Under {n} kr.",
    "filter.priceBetween": "{a}–{b} kr.",
    "filter.priceOver": "Over {n} kr.",
    "filter.price.min": "Min",
    "filter.price.max": "Maks",
    "filter.price.suggestions": "Forslag",
    "filter.price.suggestUnder": "Under {n} kr.",
    "filter.price.suggestBetween": "{a}–{b} kr.",
    "filter.size": "Størrelse",
    "filter.sizeAny": "Alle størrelser",
    "filter.reset": "Nulstil",
    "filter.results_one": "{n} match",
    "filter.results_other": "{n} match",
    "filter.noMatch": "Ingen varer matcher dine filtre.",

    // Welcome modal + site tour
    "welcome.eyebrow": "Nyt hjem, samme butik",
    "welcome.title.a": "Velkommen til vores",
    "welcome.title.b": "nye Farveladeland.",
    "welcome.body":
      "Vi har lige flyttet butikken til et nyt sted — samme Murano, nye måder at finde den på. Skal vi vise dig rundt?",
    "welcome.cta.tour": "Vis mig rundt",
    "welcome.cta.skip": "Spring over",
    "welcome.replay": "Tag rundvisningen igen",
    "tour.step": "Trin {n} af {total}",
    "tour.back": "Tilbage",
    "tour.next": "Næste",
    "tour.skip": "Afslut",
    "tour.done": "Færdig",
    "tour.toast": "Du kan altid starte rundvisningen igen fra footeren.",
    "tour.s1.eye": "Nyt",
    "tour.s1.title": "Find din Murano på 5 spørgsmål",
    "tour.s1.body":
      "Vores nye matchmaker stiller fem korte spørgsmål — sted, vægge, stemning, budget, tid — og finder de lamper, der passer til netop dig.",
    "tour.s2.eye": "Shop efter farve",
    "tour.s2.title": "Spring direkte ind i din yndlingsfarve",
    "tour.s2.body":
      "Lyserød, gul, grøn eller blå — vælg en farve og se alt vores mundblæste Murano i netop den tone.",
    "tour.s3.eye": "Søg",
    "tour.s3.title": "Find alt på et øjeblik",
    "tour.s3.body":
      "Tryk på forstørrelsesglasset (eller ⌘K) og søg på alt fra “mushroom 30 cm” til “lyserød væglampe”.",
    "tour.s4.eye": "Ønskeliste",
    "tour.s4.title": "Gem dine favoritter",
    "tour.s4.body":
      "Klik på hjertet for at gemme en lampe. Den venter på dig næste gang du kigger ind.",
    "tour.s5.eye": "Dansk / English",
    "tour.s5.title": "Skift sprog når du har lyst",
    "tour.s5.body":
      "Vi taler både dansk og engelsk — og sender stadig hver mandag, uanset hvilket sprog du vælger.",

    // Footer — information block
    "footer.info": "Information",
    "footer.shipping": "Levering",
    "footer.returns": "Retur",
    "footer.faq": "FAQ",
    "footer.terms": "Handelsbetingelser",
    "footer.privacy": "Privatlivspolitik",
    "footer.lampRequest": "Find mig en lampe",
    "footer.contact": "Skriv til butikken",

    // Cart drawer extras
    "cart.freeShippingProgress": "Tilføj {amount} kr. mere — så er fragten gratis 🎁",
    "cart.freeShippingHit": "Du har fri fragt ✓",
    "cart.trustLine": "Sikker betaling · MobilePay · Klarna · Visa · Mastercard",
    "cart.continueShopping": "Fortsæt med at shoppe",
    "cart.lookingForSpecific": "Leder du efter en bestemt lampe?",
    "cart.askUs": "Skriv og vi prøver at finde den",

    // PDP polish
    "product.deliveryEstimate": "Vi sender hver mandag. Skriv til os hvis du har brug for hurtigere ekspedition.",
    "product.payments": "Betaling",
    "product.askAbout": "Spørg om varen",
    "product.askAboutSubject": "Spørgsmål om: {title}",
    "product.soldNotice": "Den her er solgt — men vi prøver gerne at finde en lignende.",
    "product.soldCta": "Skriv dig op",
    "product.uniquePiece": "Det her stykke er det eneste — først til mølle.",

    // Lamp request flow
    "lamp.eyebrow": "Vi finder den for dig",
    "lamp.titleA": "Søger du en",
    "lamp.titleB": "bestemt lampe?",
    "lamp.intro":
      "Vi rejser jævnligt til Italien og har gode kontakter til distributører rundt omkring i Europa. Hvis du leder efter en bestemt Murano-lampe — eller flere ens — så fortæl os om den. Vi vender tilbage hvis vi finder den.",
    "lamp.howWorks": "Sådan gør vi",
    "lamp.step1.t": "Du skriver",
    "lamp.step1.b": "Beskriv lampen så godt du kan — type, farve, størrelse, antal. Et billede eller link hjælper meget.",
    "lamp.step2.t": "Vi leder",
    "lamp.step2.b": "Vi spørger vores distributører i Italien og holder øje med dem på vores rejser og loppemarkeder.",
    "lamp.step3.t": "Vi vender tilbage",
    "lamp.step3.b": "Hvis vi finder den — eller noget der ligner — skriver vi til dig med pris og billeder. Helt uforpligtende.",
    "lamp.form.name": "Navn",
    "lamp.form.email": "Mail",
    "lamp.form.phone": "Telefon (frivilligt)",
    "lamp.form.phoneHint": "Hjælper os med at give besked hurtigt, hvis vi finder noget",
    "lamp.form.description": "Hvilken lampe leder du efter?",
    "lamp.form.descriptionPlaceholder":
      "F.eks. en Murano mushroom-lampe i lyserød, ca. 30 cm bred. Helst med messingfod. Skal stå på et natbord.",
    "lamp.form.referenceUrl": "Link til billede eller forlæg (frivilligt)",
    "lamp.form.referenceUrlPlaceholder":
      "Pinterest, en anden butik, et opslag på Instagram…",
    "lamp.form.budget": "Budget cirka (frivilligt)",
    "lamp.form.budgetPlaceholder": "F.eks. 2.000–4.000 kr.",
    "lamp.form.quantity": "Hvor mange søger du?",
    "lamp.form.quantity.one": "Én lampe",
    "lamp.form.quantity.two": "Et par (to)",
    "lamp.form.quantity.three": "Tre eller flere",
    "lamp.form.submit": "Send forespørgslen",
    "lamp.form.submitting": "Sender…",
    "lamp.form.required": "Påkrævet",
    "lamp.form.error": "Noget gik galt — prøv igen, eller skriv direkte til butik@farveladeland.com",
    "lamp.form.invalid": "Tjek lige felterne — der mangler noget.",
    "lamp.thanks.eyebrow": "Tak",
    "lamp.thanks.title": "Vi har modtaget din forespørgsel.",
    "lamp.thanks.body":
      "Vi gennemgår alle henvendelser og vender tilbage hvis vi finder noget — eller hvis vi har spørgsmål. Det kan tage alt fra et par dage til et par uger, alt efter hvor specifik lampen er.",
    "lamp.thanks.cta": "Tilbage til shoppen",
    "lamp.thanks.another": "Send en mere",
    "lamp.help": "Du kan også skrive direkte til butik@farveladeland.com",

    // Legal — shared
    "legal.lastUpdated": "Senest opdateret: april 2026",
    "legal.contact.title": "Kontakt",
    "legal.contact.body":
      "Spørgsmål? Skriv til os på butik@farveladeland.com — vi svarer som regel samme dag.",

    // Shipping page
    "shipping.eyebrow": "Levering",
    "shipping.titleA": "Sendes",
    "shipping.titleB": "hver mandag.",
    "shipping.intro":
      "Vi pakker selv hver pakke — Murano i bobleplast, avis og ekstra pap. Sender fra Frederiksberg hver mandag. Større lamper og lysekroner kan have ekstra fragt på grund af volumen — vi giver besked hvis det er tilfældet.",
    "shipping.dk.title": "Danmark",
    "shipping.dk.pakkeshop": "GLS Pakkeshop",
    "shipping.dk.pakkeshopPrice": "Fra 50 kr.",
    "shipping.dk.home": "GLS Hjemmelevering",
    "shipping.dk.homePrice": "Fra 65 kr.",
    "shipping.dk.free": "Fri fragt over 500 kr.",
    "shipping.dk.note": "Pris afhænger af lampens størrelse. Den endelige fragtpris vises ved kassen.",
    "shipping.eu.title": "EU",
    "shipping.eu.body":
      "Vi sender til alle EU-lande. Pris beregnes ved kassen ud fra vægt og størrelse — typisk 150–350 kr. for en almindelig lampe.",
    "shipping.world.title": "Resten af verden",
    "shipping.world.body":
      "Vi sender også uden for EU — bl.a. til UK, USA, Australien og Asien. Pris beregnes ved kassen. Vær opmærksom på at told og lokal moms kan tilkomme i dit land.",
    "shipping.times.title": "Leveringstid",
    "shipping.times.body":
      "Vi sender hver mandag fra Frederiksberg. Forventet leveringstid efter afsendelse: 1–3 hverdage i DK, 3–7 hverdage i EU og 7–14 hverdage til resten af verden. Har du brug for hurtigere behandling eller ekspedition, så ræk endelig ud — vi prøver altid at finde en løsning. Vi er ansvarlige for alt vi sender ud og dækker uheld under fragt ud af huset.",
    "shipping.packing.title": "Indpakning",
    "shipping.packing.body":
      "Vintage Murano er skrøbeligt. Vi pakker hver lampe i bobleplast, avis og en kraftig kasse — ofte med ekstra støttepuder hvis lampen er særligt sart. Skulle der mod forventning ske skader under transport, så skriv til os med fotos hurtigst muligt, så finder vi en løsning.",

    // Returns page
    "returns.eyebrow": "Retur & fortrydelse",
    "returns.titleA": "14 dages",
    "returns.titleB": "fortrydelsesret.",
    "returns.intro":
      "Passer lampen ikke alligevel hjemme hos dig, så har du 14 dages fortrydelsesret fra den dag, du modtager pakken. Du skal blot give os besked og sende lampen tilbage i samme stand som du modtog den.",
    "returns.how.title": "Sådan returnerer du",
    "returns.how.s1": "Skriv til butik@farveladeland.com med dit ordrenummer inden for 14 dage.",
    "returns.how.s2": "Pak lampen forsvarligt — du er selv ansvarlig for, at den kommer uskadt tilbage til os. Brug gerne den oprindelige emballage.",
    "returns.how.s3": "Send pakken til Munkensvej 4, 2.tv, 2000 Frederiksberg.",
    "returns.how.s4": "Vi tilbagebetaler købsbeløbet (ekskl. fragt) inden for 14 dage, efter vi har modtaget pakken.",
    "returns.cost.title": "Returfragt & ansvar",
    "returns.cost.body":
      "Du betaler selv returfragten, og du er selv ansvarlig for at emballere lampen forsvarligt, så den kommer sikkert tilbage til os. Vi dækker uheld på alt vi sender ud af huset — men ikke på returforsendelser. Skriv altid til os først, så aftaler vi det bedste forløb.",
    "returns.condition.title": "Lampen skal være i original stand",
    "returns.condition.body":
      "Lampen skal være ubrugt og uden nye skader. Vintage-stand som beskrevet i annoncen er selvfølgelig fint — det er den måde du modtog den på.",
    "returns.exceptions.title": "Undtagelser",
    "returns.exceptions.body":
      "Specialtilpasninger udført på din anmodning kan ikke returneres. Det fortæller vi naturligvis i forvejen.",
    "returns.complaint.title": "Reklamation",
    "returns.complaint.body":
      "Skulle der være noget galt med lampen ved modtagelse — fejl der ikke er beskrevet, transportskader — så skriv til os med fotos hurtigst muligt. Vi finder en løsning. Du har 24 måneders reklamationsret efter købeloven.",

    // FAQ page
    "faq.eyebrow": "FAQ",
    "faq.titleA": "Spørgsmål",
    "faq.titleB": "vi får ofte.",
    "faq.intro":
      "Hvis du ikke finder svaret her, så skriv til butik@farveladeland.com — vi svarer som regel samme dag.",
    "faq.q1.q": "Er lamperne ægte vintage Murano?",
    "faq.q1.a":
      "Ja. Vi køber kun lamper vi selv kan stå inde for — oftest direkte fra italienske loppemarkeder, dødsboer eller distributører i Norditalien. Når en lampe har et signaturmærke eller en oprindelse vi kender, skriver vi det i annoncen.",
    "faq.q2.q": "Hvilken pære skal jeg bruge?",
    "faq.q2.a":
      "Det varierer fra lampe til lampe — fatningstype og maks. wattage står i produktbeskrivelsen. Hvis ikke, så spørg os, så finder vi det. De fleste vintage Murano bruger E14 eller E27.",
    "faq.q3.q": "Virker lampen i Danmark?",
    "faq.q3.a":
      "Alle lamper der sælges i shoppen er klar til brug i Danmark — 230V, dansk stik. Sender vi til et land med anden el-standard, skal du selv sørge for en adapter eller få lampen omgjort lokalt.",
    "faq.q4.q": "Hvordan pakker I lamperne?",
    "faq.q4.a":
      "Hver lampe pakkes i bobleplast, avis og en kraftig kasse — med ekstra støttepuder hvis lampen er særligt sart. Vi har sendt over hele verden uden problemer.",
    "faq.q5.q": "Kan jeg komme og se lampen først?",
    "faq.q5.a":
      "Ja, gerne. Vi har et lille showroom på Frederiksberg og tager imod efter aftale. Bestil en fremvisning, så finder vi en tid sammen.",
    "faq.q6.q": "Hvilke betalingsmuligheder har I?",
    "faq.q6.a":
      "MobilePay, Klarna, Visa, Mastercard og Apple Pay. Alt går gennem en sikker betalingsside.",
    "faq.q7.q": "Hvorfor er der så få mål i annoncen?",
    "faq.q7.a":
      "Vi prøver at få alle de vigtigste mål med — diameter, højde, fatningstype. Mangler du et bestemt mål, så skriv til os, så måler vi straks.",
    "faq.q8.q": "Sender I uden for EU?",
    "faq.q8.a":
      "Ja. Vi sender bl.a. til UK, USA, Australien og Asien. Vær opmærksom på at told og lokal moms kan tilkomme i dit land.",
    "faq.q9.q": "Kan I finde en bestemt lampe for mig?",
    "faq.q9.a":
      "Ofte ja. Vi har gode kontakter til distributører i Italien. Skriv til os via 'Find mig en lampe'-formularen — det er helt uforpligtende.",
    "faq.q10.q": "Hvad hvis lampen går i stykker under transport?",
    "faq.q10.a":
      "Skriv til os med fotos så hurtigt som muligt. Vi forsikrer alle pakker, og vi finder en løsning — enten reparation, prisnedslag eller fuld refundering.",

    // Terms page
    "terms.eyebrow": "Handelsbetingelser",
    "terms.titleA": "Handels-",
    "terms.titleB": "betingelser.",
    "terms.intro":
      "Disse betingelser gælder for køb af varer på farveladeland.com. Vi følger den danske købelov og forbrugeraftaleloven.",
    "terms.seller.title": "Sælger",
    "terms.seller.body":
      "Farveladeland\nMunkensvej 4, 2.tv\n2000 Frederiksberg\nDanmark\nbutik@farveladeland.com",
    "terms.prices.title": "Priser",
    "terms.prices.body":
      "Alle priser er i danske kroner og inkluderer 25% moms. Fragt beregnes separat ved kassen og afhænger af lampens størrelse og modtageradresse.",
    "terms.payment.title": "Betaling",
    "terms.payment.body":
      "Vi modtager MobilePay, Klarna, Visa, Mastercard og Apple Pay. Beløbet trækkes når ordren afsendes.",
    "terms.delivery.title": "Levering",
    "terms.delivery.body":
      "Vi sender hver mandag fra Frederiksberg. Inden for Danmark kan du forvente pakken 1–3 hverdage efter afsendelse. Se vores leveringsside for detaljer.",
    "terms.withdrawal.title": "Fortrydelsesret",
    "terms.withdrawal.body":
      "Du har 14 dages fortrydelsesret fra modtagelsen af varen. Se vores returside for detaljer om hvordan du gør.",
    "terms.complaint.title": "Reklamation",
    "terms.complaint.body":
      "Du har 24 måneders reklamationsret jf. købeloven. Reklamationer skal foretages inden for rimelig tid efter at fejlen er opdaget.",
    "terms.disputes.title": "Klagemuligheder",
    "terms.disputes.body":
      "Hvis du er utilfreds og vi ikke kan finde en løsning, kan du indgive en klage til Forbrugerklagenævnet (forbrug.dk) eller via EU's online klageportal: https://ec.europa.eu/consumers/odr",
    "terms.law.title": "Lovvalg",
    "terms.law.body": "Køb hos Farveladeland er underlagt dansk lovgivning.",

    // Privacy
    "privacy.eyebrow": "Privatliv",
    "privacy.titleA": "Privatlivs-",
    "privacy.titleB": "politik.",
    "privacy.intro":
      "Vi behandler dine personoplysninger med respekt og kun til de formål du har givet samtykke til. Her er hvad vi samler ind, hvorfor, og hvad du kan gøre.",
    "privacy.collect.title": "Hvad vi samler ind",
    "privacy.collect.body":
      "Når du handler hos os: navn, leveringsadresse, mail, telefonnummer, ordrehistorik. Når du skriver til os: din mail og besked. Når du besøger sitet: anonyme statistikoplysninger om sider du besøger (ingen personhenførbare data).",
    "privacy.why.title": "Hvorfor",
    "privacy.why.body":
      "Vi bruger oplysningerne til at gennemføre dit køb, sende dig pakken, svare på dine henvendelser og overholde bogføringsloven. Vi sender ikke nyhedsbreve uden dit samtykke.",
    "privacy.share.title": "Hvem deler vi med",
    "privacy.share.body":
      "Med fragtfirmaer (GLS) for at få pakken frem. Med betalingsudbydere for at modtage betaling. Med vores hosting- og e-handelsudbydere (Shopify) for at drive shoppen. Vi sælger aldrig dine data.",
    "privacy.retention.title": "Opbevaring",
    "privacy.retention.body":
      "Ordreoplysninger opbevares i 5 år af hensyn til bogføringsloven. Henvendelser slettes efter 2 år.",
    "privacy.rights.title": "Dine rettigheder",
    "privacy.rights.body":
      "Du kan til enhver tid bede om indsigt i, rettelse eller sletning af dine oplysninger. Skriv til butik@farveladeland.com. Du kan også klage til Datatilsynet (datatilsynet.dk).",
    "privacy.cookies.title": "Cookies",
    "privacy.cookies.body":
      "Nødvendige cookies og lokal lagring får shoppen til at fungere — bl.a. din kurv, checkout, ønskeliste og dit cookie-valg. Kun med dit samtykke bruger vi derudover: præferencer (sete produkter og seneste søgninger, gemt på din enhed), statistik (Google Analytics og en anonym visningstæller; Google kan behandle data i USA) og marketing (deling af besøg og køb med annoncepartnere som Google og via Shopify). Intet af dette indlæses, før du har givet samtykke. Dit valg gemmes i 12 måneder, og du kan til enhver tid ændre eller trække det tilbage under “Cookie-indstillinger” nederst på siden.",

    // Newsletter
    "newsletter.eyebrow": "Nyhedsbrev",
    "newsletter.title": "Få besked når der lander nye fund",
    "newsletter.body":
      "Vi sender en kort mail når der kommer nye Murano-lamper og loppefund i shoppen. Ingen spam — og du kan altid melde dig fra.",
    "newsletter.placeholder": "Din e-mail",
    "newsletter.cta": "Tilmeld",
    "newsletter.consent": "Vi bruger din mail til nyhedsbrevet — intet andet.",

    // Cookie banner
    "cookie.aria": "Cookie-samtykke",
    "cookie.eyebrow": "Cookies",
    "cookie.title": "Denne side bruger cookies",
    "cookie.body":
      "Nødvendige cookies får shoppen til at virke. Med dit samtykke bruger vi også cookies til at huske dine præferencer, lave statistik og måle markedsføring. Du kan altid ændre dit valg under “Cookie-indstillinger” nederst på siden.",
    "cookie.readMore": "Læs mere",
    "cookie.accept": "Accepter alle",
    "cookie.reject": "Afvis alle",
    "cookie.customize": "Tilpas",
    "cookie.save": "Gem valg",
    "cookie.alwaysOn": "Altid aktiv",
    "cookie.close": "Luk",
    "cookie.settings": "Cookie-indstillinger",
    "cookie.necessary.title": "Nødvendige",
    "cookie.necessary.body":
      "Får shoppen til at virke: kurv, checkout, ønskeliste og dit cookie-valg. Kan ikke slås fra.",
    "cookie.preferences.title": "Præferencer",
    "cookie.preferences.body":
      "Husker produkter du har set og dine seneste søgninger, så du nemt finder dem igen. Gemmes kun på din enhed.",
    "cookie.statistics.title": "Statistik",
    "cookie.statistics.body":
      "Google Analytics og en anonym visningstæller viser os, hvilke sider og produkter der bliver set. Google kan behandle data i USA.",
    "cookie.marketing.title": "Marketing",
    "cookie.marketing.body":
      "Deler dine besøg og køb med vores annoncepartnere (bl.a. Google og via Shopify), så vi kan måle og målrette annoncer.",

    // Sommerudsalg-popup
    "sale.aria": "Sommerudsalg",
    "sale.eyebrow": "Nyt hos os",
    "sale.title": "Sommerudsalg",
    "sale.body": "Se vores nye Sommerudsalg-kollektion — udvalgte vintage-fund med 27% rabat, som trækkes automatisk fra i kassen.",
    "sale.cta": "Se Sommerudsalg",
    "sale.close": "Luk",
    "sale.pill": "Sommerudsalg",
    "sale.pillAria": "Åbn Sommerudsalg",
    "sale.badge": "−27%",

    "newsletter.success": "Tak — du er på listen.",
    "newsletter.error": "Noget gik galt. Prøv igen om lidt.",
    "newsletter.invalid": "Tjek lige din e-mail én gang til.",

    // Exit intent
    "exit.eyebrow": "Inden du går",
    "exit.title": "Vil du have de nye fund først?",
    "exit.body":
      "Vores Murano-lamper bliver tit revet væk samme dag. Skriv dig op, så får du en mail når der lander nye stykker.",
    "exit.dismiss": "Nej tak",

    // Product CRO
    "product.unique": "Én af én — vintage",
    "product.delivery": "Leveres typisk 3–7 hverdage i Danmark",
    "product.expressBuy": "Køb nu",
    "product.lastOne": "Sidste eksemplar",
  },

  en: {
    "topbar.dispatch": "We ship every Monday",
    "topbar.flea": "New flea finds every day",
    "topbar.book": "Book a viewing",
    "topbar.sale": "27% off Summer Sale — applied automatically at checkout",


    "header.estMurano": "Vintage Murano",
    "header.basket": "Basket",
    "header.about": "About",
    "header.menuOpen": "Open menu",
    "header.menuClose": "Close menu",
    "header.langSwitch": "Switch language",
    "header.newBadge": "New",
    "header.japanCollection": "Finds from Japan",

    "nav.murano": "Murano",
    "nav.lamps": "Lamps",
    "nav.tableware": "Tableware",
    "nav.glass": "Glass",
    "nav.interior": "Interior",
    "nav.sale": "Sale",
    "nav.browseAll":
      "Browse all our {label} — handpicked from flea markets, estates and trips to Italy.",

    "footer.tagline.line1": "Secondhand, vintage",
    "footer.tagline.line2": "& Murano glass.",
    "footer.about":
      "Farveladeland is a small Danish shop for vintage Murano lamps and other colourful finds from flea markets around the world.",
    "footer.wander": "Shortcuts",
    "footer.editMurano": "Murano",
    "footer.allCollections": "All goods",
    "footer.showroom": "Showroom",
    "footer.story": "About us",
    "footer.bookViewing": "Book a viewing",
    "footer.leksikon": "Murano encyclopedia",
    "footer.visit": "Visit us",
    "footer.address":
      "Munkensvej 4, 2.tv\n2000 Frederiksberg, Denmark\nbutik@farveladeland.com",
    "footer.instagram": "Instagram",
    "footer.newsletter": "Newsletter",
    "footer.copy": "Farveladeland · Worldwide shipping",
    "footer.handblown": "Vintage Murano · Curated in Copenhagen",

    "home.eyebrow": "Secondhand · Vintage · Murano",
    "home.hero.l1": "Secondhand,",
    "home.hero.l2": "vintage",
    "home.hero.l3": "& Murano glass.",
    "home.hero.body":
      "Vintage Murano lamps and colourful finds from flea markets, estates and trips around Europe. Every piece is one of a kind — when it's gone, it's gone.",
    "home.hero.cta1": "See the Murano selection",
    "home.hero.cta2": "Book a viewing",
    "home.hero.strip1": "We ship every Monday",
    "home.hero.strip2": "Worldwide shipping",
    "home.hero.strip3": "Showroom in Frederiksberg",

    "home.outlet.eyebrow": "Murano outlet",
    "home.outlet.titleA": "Save",
    "home.outlet.titleB": "15–45 % on selected pieces.",
    "home.outlet.body":
      "A rotating selection of Murano lamps with small flaws or simply ready for a new home.",
    "home.outlet.cta": "See the selection",
    "home.outlet.imgAlt": "Murano outlet",

    "home.pillars.showroom.title": "Viewing",
    "home.pillars.showroom.sub": "Showroom",
    "home.pillars.showroom.text": "You can book a time to visit our showroom in Frederiksberg.",
    "home.pillars.showroom.cta": "Book a time",
    "home.pillars.all.title": "All goods",
    "home.pillars.all.sub": "Shop",
    "home.pillars.all.text": "Browse the entire selection.",
    "home.pillars.all.cta": "Take a look",
    "home.pillars.wedding.title": "Wedding service",
    "home.pillars.wedding.sub": "Rent for the party",
    "home.pillars.wedding.text": "See our full set-up for the wedding here.",
    "home.pillars.wedding.cta": "Read more",

    "home.murano.eyebrow": "The Murano collection",
    "home.murano.titleA": "Three kinds",
    "home.murano.titleB": "of Murano lamp.",
    "home.murano.seeAll": "See all",
    "home.murano.shopColl": "See the collection",
    "home.murano.ceiling": "Murano ceiling lamps",
    "home.murano.mushroom": "Murano mushroom lamps",
    "home.murano.wall": "Murano wall lamps",

    "home.justin.eyebrow": "Just landed",
    "home.justin.title": "New in.",
    "home.justin.seeAll": "See all",
    "home.justin.loading": "Loading…",

    "home.hero.featured": "This week's lamp",

    "home.colorland.eyebrow": "Top 10",
    "home.colorland.titleA": "Most viewed",
    "home.colorland.titleB": "this week.",
    "home.colorland.body":
      "What our visitors are clicking on right now — refreshed daily. Catch them before they're gone.",
    "home.colorland.empty": "Loading this week's favourites…",

    "home.colorshop.eyebrow": "Shop by colour",
    "home.colorshop.titleA": "Find your",
    "home.colorshop.titleB": "favourite hue.",
    "home.colorshop.body":
      "Pick a colour and see every hand-blown Murano lamp, glass and find in that exact tone.",

    "colors.eyebrow": "Shop by colour",
    "colors.titleA": "Eight colours,",
    "colors.titleB": "one land.",
    "colors.body":
      "Choose a tone and find lamps, glass and finds in that exact colour — curated from our shelves in Frederiksberg.",
    "colors.count": "{n} colours",

    "color.crumbHome": "Home",
    "color.crumbColors": "Colours",
    "color.empty": "Nothing in this colour right now — check back in a few days.",
    "color.seeAll": "See all Murano lamps",
    "color.allColors": "All colours",
    "color.count": "{n} finds",

    "sizes.eyebrow": "Shop by size",
    "sizes.titleA": "Lamps in",
    "sizes.titleB": "your size.",
    "sizes.body":
      "Find lamps, pendants and chandeliers by diameter — from small 18 cm table lamps to large pendants of 50 cm and up. We read dimensions straight from each product description.",
    "sizes.count": "{n} sizes",
    "sizes.unit": "Diameter, Ø",
    "size.crumbHome": "Home",
    "size.crumbSizes": "Size",
    "size.empty": "No lamps in this size right now — check back in a few days.",
    "size.seeAll": "See all Murano lamps",
    "size.allSizes": "All sizes",
    "size.count": "{n} lamps",
    "size.range": "Diameter Ø",

    "home.scenes.ceiling":
      "Pendants, plafonds and chandeliers — the first thing you notice when you walk in.",
    "home.scenes.mushroom":
      "The small round lamp on the bedside table that turns a bedroom into a bedroom.",
    "home.scenes.wall":
      "A quiet light in the hallway, by the bed, or where you read.",

    "home.curator.eyebrow": "Find of the week",
    "home.curator.titleA": "Hand-",
    "home.curator.titleB": "picked.",
    "home.curator.quote":
      "This one came home from a flea market outside Bologna. The pink is unusual — most '70s Murano leans amber. It just landed on the shelf.",
    "home.curator.signoff": "— A note from the shop",
    "home.curator.thisWeek": "This week's pick",
    "home.curator.cta": "See the piece",


    "collections.eyebrow": "All goods",
    "collections.titleA": "The full",
    "collections.titleB": "selection.",
    "collections.body":
      "{n} collections of vintage Murano, glass, porcelain and other colourful finds from European flea markets.",
    "collections.count": "{n} collections",
    "collections.browseAll": "Browse all products",

    "collection.back": "← All collections",
    "collection.eyebrow": "Farveladeland",
    "collection.empty": "Nothing in this collection right now.",
    "collection.browseOther": "Browse other collections",
    "collection.piece_one": "{n} item",
    "collection.piece_other": "{n} items",
    "collection.notFound.eyebrow": "Empty shelf",
    "collection.notFound.title": "We couldn't find that collection",
    "collection.notFound.body": "It may have been moved or renamed.",
    "collection.notFound.cta": "Back to all collections",
    "collection.error.eyebrow": "Oops",
    "collection.error.title": "We couldn't load this collection",
    "collection.error.cta": "Try again",
    "collection.about": "About this collection",
    "collection.highlights": "Good to know",
    "collection.fallbackIntro":
      "A small, curated selection of vintage and secondhand pieces from Farveladeland. We dispatch every Monday from Frederiksberg.",

    "product.back": "← Back to the shop",
    "product.eyebrow": "Vintage · Farveladeland",
    "product.variant": "Variant",
    "product.add": "Add to basket",
    "product.sold": "Sold out",
    "product.about": "Description",
    "product.dispatch": "Dispatch",
    "product.dispatchVal": "Every Monday",
    "product.shipping": "Shipping",
    "product.shippingVal": "Worldwide",
    "product.related.eyebrow": "Just landed",
    "product.related.title": "More from the shop",
    "product.notFound.eyebrow": "It found a new home",
    "product.notFound.title": "This piece is no longer in stock",
    "product.notFound.cta": "Browse other items",
    "product.error.title": "We couldn't load this piece",
    "product.error.cta": "Try again",

    "murano.eyebrow": "Murano",
    "murano.titleA": "Vintage Murano",
    "murano.titleB": "from Italy,",
    "murano.titleC": "curated in Copenhagen.",
    "murano.body":
      "We travel to Italy regularly and bring home Murano lamps and glass we'd want in our own home. Everything is secondhand, everything is unique — and we pack each piece carefully before it heads out into the world.",
    "murano.shopCta": "See the collection",
    "murano.editions.01.title": "Murano ceiling lamps",
    "murano.editions.01.italian": "Lampadari",
    "murano.editions.01.text":
      "We always have plenty of beautiful Murano ceiling lamps in the shop — pendants, plafonniers and chandeliers in everything from white swirls to pastel ribbons.",
    "murano.editions.01.range": "From 4,500 DKK",
    "murano.editions.02.title": "Murano mushroom lamps",
    "murano.editions.02.italian": "Funghi",
    "murano.editions.02.text":
      "We always have plenty of beautiful Murano mushroom lamps in the shop — in pink, white, butter yellow and pale blue.",
    "murano.editions.02.range": "From 1,800 DKK",
    "murano.editions.03.title": "Murano wall lamps",
    "murano.editions.03.italian": "Appliques",
    "murano.editions.03.text":
      "We always have plenty of beautiful Murano wall lamps in the shop — perfect for an entry, hallway or beside the bed.",
    "murano.editions.03.range": "From 2,400 DKK",
    "murano.fact.1.k": "1291",
    "murano.fact.1.v": "The year the glassmakers were moved to Murano so Venice wouldn't burn down.",
    "murano.fact.2.k": "1,450°C",
    "murano.fact.2.v": "The temperature where colour, gold leaf and air melt into one.",
    "murano.fact.3.k": "One of a kind",
    "murano.fact.3.v": "Every piece is unique. When it's sold, it's sold.",

    "match.eyebrow": "Find your Murano",
    "match.cta": "Help me choose",
    "match.ctaSub": "5 questions · about 30 seconds",

    "showroom.eyebrow": "Showroom · Frederiksberg",
    "showroom.titleA": "Drop by",
    "showroom.titleB": "the showroom.",
    "showroom.appt.eyebrow": "By appointment",
    "showroom.appt.title": "See the pieces in person.",
    "showroom.appt.body1":
      "Our showroom in Frederiksberg is small and cosy. We see one party at a time, so you can switch the lamps on, hold the porcelain and see everything in natural light.",
    "showroom.appt.body2":
      "Send us a message on Instagram or email and we'll find a time.",
    "showroom.appt.cta": "Book a viewing",
    "showroom.info.address.k": "Address",
    "showroom.info.address.v": "Munkensvej 4, 2.tv\n2000 Frederiksberg",
    "showroom.info.hours.k": "Hours",
    "showroom.info.hours.v": "By appointment\nUsually weekdays after 5 pm",
    "showroom.info.dispatch.k": "Dispatch",
    "showroom.info.dispatch.v": "Every Monday\nWorldwide shipping",

    "about.eyebrow": "About us",
    "about.titleA": "We collect",
    "about.titleB": "the colourful.",
    "about.intro":
      "Farveladeland — \"the land of colour\" — is a small shop for vintage Murano and other colourful finds from flea markets around the world.",
    "about.p1":
      "We travel often, mostly to small Italian towns, and drive home with cars full of carefully wrapped lamps, glass and porcelain.",
    "about.p2":
      "Back in Copenhagen we clean each piece, photograph it in natural light and write down everything we know about it. Then it goes in the shop and waits to find the right home.",
    "about.p3":
      "Nothing here is mass-produced. Nothing here is the same. When a piece is gone, it's gone — and that's how we like it.",
    "about.believe.eyebrow": "How we work",
    "about.believe.titleA": "Slow trade,",
    "about.believe.titleB": "long love.",
    "about.believe.l1": "Every piece is photographed in honest daylight, so you see exactly what you get — flaws and all.",
    "about.believe.l2": "We pack thoroughly. Murano travels in bubble wrap, newspaper and extra cardboard.",
    "about.believe.l3": "If it doesn't feel right at home, you can return it within 14 days.",

    "visit.eyebrow": "Book a viewing",
    "visit.titleA": "Drop by",
    "visit.titleB": "the showroom.",
    "visit.body":
      "Tell us when you'd like to come by and what you're looking for. We'll get back to you as soon as we can with a time and the address.",
    "visit.info.hours.k": "Hours",
    "visit.info.hours.v": "By appointment",
    "visit.info.email.k": "Email",
    "visit.info.email.v": "butik@farveladeland.com",
    "visit.info.ig.k": "Instagram",
    "visit.info.ig.v": "@farveladeland",
    "visit.info.dispatch.k": "Dispatch",
    "visit.info.dispatch.v": "Every Monday · worldwide",
    "visit.form.name": "Name",
    "visit.form.email": "Email",
    "visit.form.date": "Preferred date",
    "visit.form.message": "What are you looking for?",
    "visit.form.placeholder":
      "A ceiling lamp for the dining room, anything in teal, a chandelier under 60 cm…",
    "visit.form.submit": "Send",
    "visit.thanks.eyebrow": "Thanks",
    "visit.thanks.title": "We've got your message.",
    "visit.thanks.body": "We'll get back to you as soon as we can with a time and the address.",

    "cart.title": "Your basket",
    "cart.empty": "Your basket is empty.",
    "cart.items_one": "{n} item in the basket.",
    "cart.items_other": "{n} items in the basket.",
    "cart.emptyHelp": "Nothing in the basket yet — find something in the shop.",
    "cart.browse": "To the shop",
    "cart.subtotal": "Subtotal",
    "cart.subtotalNote": "Incl. VAT. Shipping calculated at checkout.",
    "cart.checkout": "Checkout",
    "cart.decrease": "Fewer",
    "cart.increase": "More",
    "cart.remove": "Remove",
    "cart.added": "Added to basket",
    "cart.viewCart": "View basket",

    "header.shop": "Shop",
    "header.murano": "Murano",
    "header.colors": "Colours",
    "header.sizes": "Size",
    "header.showroom": "Showroom",
    "header.match": "Help me choose",
    "header.blog": "Guides",
    "header.allGoods": "All goods",
    "header.allProducts": "All products",
    "home.allProducts.cta": "Browse all products",
    "home.allProducts.sub": "Explore the whole shop across categories.",
    "header.shopByColor": "Shop by colour",
    "header.shopBySize": "Shop by size",
    "header.allColors": "See all colours",
    "header.allSizes": "See all sizes",
    "header.search": "Search",
    "header.searchPlaceholder": "Search by colour, shape or size…",
    "header.searchHint": "Press ⌘K to search",
    "search.products": "Products",
    "search.categories": "Categories",
    "search.sizes": "Sizes",
    "search.colors": "Colours",
    "search.recent": "Recent searches",
    "search.popular": "Popular searches",
    "search.empty": "No results for {q}",
    "search.emptyHint": "Try a colour like \"pink\" or a shape like \"mushroom\".",
    "search.viewAll": "See all {n} results",
    "search.title": "Search results",
    "search.resultsFor": "Results for \"{q}\"",
    "search.resultsCount": "{n} products found",
    "search.noResults": "No results",
    "search.tryAnother": "Try another search, or browse by colour or size from the menu.",
    "search.close": "Close search",
    "search.clear": "Clear",


    "trust.dispatch.k": "Ships every Monday",
    "trust.dispatch.v": "Wrapped in newspaper, bubble wrap and extra cardboard.",
    "trust.shipping.k": "Worldwide",
    "trust.shipping.v": "We ship to all EU countries and the rest of the world.",
    "trust.returns.k": "14-day returns",
    "trust.returns.v": "If it doesn't feel right at home, you can send it back.",
    "trust.unique.k": "One of a kind",
    "trust.unique.v": "Every piece is unique — when it's gone, it's gone.",

    "founder.eyebrow": "A note from the shop",
    "founder.title": "We pack every parcel ourselves.",
    "founder.body":
      "Farveladeland is a small shop. We travel out ourselves, photograph each piece in daylight and pack every parcel in newspaper and bubble wrap. Write to us if you're unsure about a lamp — we usually reply the same day.",
    "founder.cta": "Write to us",

    "product.dimensions": "Details",
    "product.zoom": "Click to zoom",
    "product.zoomClose": "Close zoom",
    "product.imageOf": "Image {n} of {total}",
    "product.prevImage": "Previous image",
    "product.nextImage": "Next image",
    "product.stock.in": "In stock — only one piece",
    "product.stock.out": "Sold out",
    "product.shareTitle": "Share",

    "notFound.eyebrow": "Empty shelf",
    "notFound.body":
      "This page doesn't exist. It may have been sold, moved — or never been here at all.",
    "notFound.cta": "Back to home",

    "badge.new": "Just landed",
    "badge.sold": "Sold out",
    "badge.sale": "Sale",
    "price.was": "Was {price}",

    "wishlist.add": "Save to wishlist",
    "wishlist.remove": "Remove from wishlist",
    "wishlist.title": "My wishlist",
    "wishlist.eyebrow": "Saved for later",
    "wishlist.empty": "You haven't saved anything yet.",
    "wishlist.emptyHelp":
      "Tap the heart on a piece you want to keep an eye on — you'll find it here again.",
    "wishlist.browse": "Browse the shop",
    "wishlist.count_one": "{n} saved piece",
    "wishlist.count_other": "{n} saved pieces",
    "wishlist.clear": "Clear wishlist",
    "wishlist.unavailable":
      "This one is no longer available — other finds are waiting, though.",
    "wishlist.nav": "Wishlist",
    "wishlist.toastAdded": "Saved to your wishlist",
    "wishlist.toastRemoved": "Removed from wishlist",
    "wishlist.toastAction": "View wishlist",
    "price.from": "From {price}",

    "recent.title": "Recently viewed",
    "recent.eyebrow": "You were looking at",
    "recent.clear": "Clear",

    "filter.title": "Filter & sort",
    "filter.sort": "Sort",
    "filter.sort.featured": "Recommended",
    "filter.sort.newest": "Newest first",
    "filter.sort.priceAsc": "Price: low to high",
    "filter.sort.priceDesc": "Price: high to low",
    "filter.sort.titleAsc": "Title A–Z",
    "filter.availability": "Availability",
    "filter.inStock": "In stock only",
    "filter.price": "Price",
    "filter.priceAny": "All prices",
    "filter.priceUnder": "Under {n} DKK",
    "filter.priceBetween": "{a}–{b} DKK",
    "filter.priceOver": "Over {n} DKK",
    "filter.price.min": "Min",
    "filter.price.max": "Max",
    "filter.price.suggestions": "Suggestions",
    "filter.price.suggestUnder": "Under {n} DKK",
    "filter.price.suggestBetween": "{a}–{b} DKK",
    "filter.size": "Size",
    "filter.sizeAny": "All sizes",
    "filter.reset": "Reset",
    "filter.results_one": "{n} match",
    "filter.results_other": "{n} matches",
    "filter.noMatch": "No items match your filters.",

    // Welcome modal + site tour
    "welcome.eyebrow": "New home, same shop",
    "welcome.title.a": "Welcome to our",
    "welcome.title.b": "new Farveladeland.",
    "welcome.body":
      "We've just moved the shop to a new home — same Murano, new ways to find it. Want a quick tour?",
    "welcome.cta.tour": "Show me around",
    "welcome.cta.skip": "Skip",
    "welcome.replay": "Take the tour again",
    "tour.step": "Step {n} of {total}",
    "tour.back": "Back",
    "tour.next": "Next",
    "tour.skip": "Close",
    "tour.done": "Done",
    "tour.toast": "You can always restart the tour from the footer.",
    "tour.s1.eye": "New",
    "tour.s1.title": "Find your Murano in 5 questions",
    "tour.s1.body":
      "Our new matchmaker asks five short questions — place, walls, feeling, budget, timing — and picks the lamps that fit you.",
    "tour.s2.eye": "Shop by colour",
    "tour.s2.title": "Jump straight to your favourite colour",
    "tour.s2.body":
      "Rosa antico, salvia, burro or cielo — pick a tone and see all the hand-blown Murano in that exact shade.",
    "tour.s3.eye": "Search",
    "tour.s3.title": "Find anything in a moment",
    "tour.s3.body":
      "Hit the magnifying glass (or ⌘K) and search for anything from “mushroom 30 cm” to “pink wall lamp”.",
    "tour.s4.eye": "Wishlist",
    "tour.s4.title": "Save your favourites",
    "tour.s4.body":
      "Tap the heart to save a lamp. It'll be waiting for you the next time you stop by.",
    "tour.s5.eye": "Dansk / English",
    "tour.s5.title": "Switch language whenever you like",
    "tour.s5.body":
      "We speak both Danish and English — and we still ship every Monday, whichever you choose.",

    // Footer — information block
    "footer.info": "Information",
    "footer.shipping": "Shipping",
    "footer.returns": "Returns",
    "footer.faq": "FAQ",
    "footer.terms": "Terms of sale",
    "footer.privacy": "Privacy policy",
    "footer.lampRequest": "Find me a lamp",
    "footer.contact": "Write to the shop",

    // Cart drawer extras
    "cart.freeShippingProgress": "Add {amount} DKK more — and shipping is free 🎁",
    "cart.freeShippingHit": "You've got free shipping ✓",
    "cart.trustLine": "Secure checkout · MobilePay · Klarna · Visa · Mastercard",
    "cart.continueShopping": "Continue shopping",
    "cart.lookingForSpecific": "Looking for a specific lamp?",
    "cart.askUs": "Ask us — we often find them",

    // PDP polish
    "product.deliveryEstimate": "We dispatch every Monday. Write to us if you need faster handling.",
    "product.payments": "Payment",
    "product.askAbout": "Ask about this piece",
    "product.askAboutSubject": "Question about: {title}",
    "product.soldNotice": "This one is sold — but we'll happily try to find you a similar piece.",
    "product.soldCta": "Let us know what you want",
    "product.uniquePiece": "This is the only one — first come, first served.",

    // Lamp request flow
    "lamp.eyebrow": "We'll find it for you",
    "lamp.titleA": "Looking for a",
    "lamp.titleB": "specific lamp?",
    "lamp.intro":
      "We travel to Italy regularly and have good contacts with distributors across Europe. If you're looking for a particular Murano lamp — or several matching ones — tell us about it. We'll get back to you if we find it.",
    "lamp.howWorks": "How it works",
    "lamp.step1.t": "You write",
    "lamp.step1.b": "Describe the lamp as best you can — type, colour, size, quantity. A photo or link helps a lot.",
    "lamp.step2.t": "We search",
    "lamp.step2.b": "We ask our distributors in Italy and keep an eye out on our trips and at flea markets.",
    "lamp.step3.t": "We get back to you",
    "lamp.step3.b": "If we find it — or something similar — we'll email you with photos and a price. No obligation.",
    "lamp.form.name": "Name",
    "lamp.form.email": "Email",
    "lamp.form.phone": "Phone (optional)",
    "lamp.form.phoneHint": "Helps us reach you quickly if we find something",
    "lamp.form.description": "What lamp are you looking for?",
    "lamp.form.descriptionPlaceholder":
      "E.g. a Murano mushroom lamp in pink, around 30 cm wide. Brass base preferred. Going on a bedside table.",
    "lamp.form.referenceUrl": "Link to a photo or reference (optional)",
    "lamp.form.referenceUrlPlaceholder":
      "Pinterest, another shop, an Instagram post…",
    "lamp.form.budget": "Approximate budget (optional)",
    "lamp.form.budgetPlaceholder": "E.g. 2,000–4,000 DKK",
    "lamp.form.quantity": "How many are you looking for?",
    "lamp.form.quantity.one": "Just one",
    "lamp.form.quantity.two": "A pair (two)",
    "lamp.form.quantity.three": "Three or more",
    "lamp.form.submit": "Send the request",
    "lamp.form.submitting": "Sending…",
    "lamp.form.required": "Required",
    "lamp.form.error": "Something went wrong — try again, or write us directly at butik@farveladeland.com",
    "lamp.form.invalid": "Check the fields — something's missing.",
    "lamp.thanks.eyebrow": "Thanks",
    "lamp.thanks.title": "We've got your request.",
    "lamp.thanks.body":
      "We read every request and get back to you if we find something — or if we have questions. It can take anywhere from a few days to a few weeks, depending on how specific the lamp is.",
    "lamp.thanks.cta": "Back to the shop",
    "lamp.thanks.another": "Send another",
    "lamp.help": "You can also write directly to butik@farveladeland.com",

    // Legal — shared
    "legal.lastUpdated": "Last updated: April 2026",
    "legal.contact.title": "Contact",
    "legal.contact.body":
      "Questions? Write to butik@farveladeland.com — we usually reply the same day.",

    // Shipping page
    "shipping.eyebrow": "Shipping",
    "shipping.titleA": "Shipped",
    "shipping.titleB": "every Monday.",
    "shipping.intro":
      "We pack every parcel ourselves — Murano in bubble wrap, newspaper and extra cardboard. Sent from Frederiksberg every Monday. Larger lamps and chandeliers may carry an additional shipping charge due to volume — we'll let you know if that's the case.",
    "shipping.dk.title": "Denmark",
    "shipping.dk.pakkeshop": "GLS Parcel Shop",
    "shipping.dk.pakkeshopPrice": "From 50 DKK",
    "shipping.dk.home": "GLS Home Delivery",
    "shipping.dk.homePrice": "From 65 DKK",
    "shipping.dk.free": "Free shipping over 500 DKK",
    "shipping.dk.note": "Price depends on the lamp's size. The final shipping cost is shown at checkout.",
    "shipping.eu.title": "EU",
    "shipping.eu.body":
      "We ship to all EU countries. Price is calculated at checkout based on weight and size — typically 150–350 DKK for a standard lamp.",
    "shipping.world.title": "Rest of the world",
    "shipping.world.body":
      "We also ship outside the EU — including the UK, USA, Australia and Asia. Price is calculated at checkout. Please note that customs duties and local VAT may apply in your country.",
    "shipping.times.title": "Delivery time",
    "shipping.times.body":
      "We dispatch every Monday from Frederiksberg. Expected delivery after dispatch: 1–3 business days in DK, 3–7 days in the EU and 7–14 days for the rest of the world. Need faster handling or dispatch? Reach out — we always try to find a solution. We're responsible for everything we send out and cover any damage during outbound transit.",
    "shipping.packing.title": "Packing",
    "shipping.packing.body":
      "Vintage Murano is fragile. We pack each lamp in bubble wrap, newspaper and a sturdy box — often with extra padding if the lamp is particularly delicate. If anything should arrive damaged, write to us with photos as soon as possible and we'll find a solution.",

    // Returns page
    "returns.eyebrow": "Returns & withdrawal",
    "returns.titleA": "14-day right",
    "returns.titleB": "of withdrawal.",
    "returns.intro":
      "If the lamp doesn't feel right at home, you have 14 days from receipt to change your mind. Just let us know and send the lamp back in the same condition you received it.",
    "returns.how.title": "How to return",
    "returns.how.s1": "Email butik@farveladeland.com with your order number within 14 days.",
    "returns.how.s2": "Pack the lamp securely — you're responsible for it reaching us undamaged. Use the original packaging if possible.",
    "returns.how.s3": "Ship to Munkensvej 4, 2.tv, 2000 Frederiksberg, Denmark.",
    "returns.how.s4": "We'll refund the purchase price (excluding shipping) within 14 days of receiving the parcel.",
    "returns.cost.title": "Return shipping & responsibility",
    "returns.cost.body":
      "You pay return shipping yourself, and you're responsible for packing the lamp securely so it gets back to us undamaged. We cover any mishaps on everything we send out — but not on return shipments. Always write to us first and we'll agree on the best route.",
    "returns.condition.title": "The lamp must be in original condition",
    "returns.condition.body":
      "The lamp must be unused and free of new damage. Vintage condition as described in the listing is of course fine — that's how you received it.",
    "returns.exceptions.title": "Exceptions",
    "returns.exceptions.body":
      "Any custom modifications made at your request cannot be returned. We'll let you know in advance.",
    "returns.complaint.title": "Defects",
    "returns.complaint.body":
      "If anything is wrong with the lamp on arrival — undescribed defects, transport damage — write to us with photos as soon as possible. We'll find a solution. You have a 24-month warranty under Danish consumer law.",

    // FAQ page
    "faq.eyebrow": "FAQ",
    "faq.titleA": "Questions",
    "faq.titleB": "we get often.",
    "faq.intro":
      "If you can't find your answer here, write to butik@farveladeland.com — we usually reply the same day.",
    "faq.q1.q": "Are the lamps genuine vintage Murano?",
    "faq.q1.a":
      "Yes. We only buy lamps we can vouch for ourselves — most often directly from Italian flea markets, estates or distributors in northern Italy. When a lamp has a signature or a known provenance, we note it in the listing.",
    "faq.q2.q": "Which bulb should I use?",
    "faq.q2.a":
      "It varies from lamp to lamp — fitting type and maximum wattage are listed in each product description. If they're missing, ask us and we'll find out. Most vintage Murano lamps use E14 or E27.",
    "faq.q3.q": "Will the lamp work in my country?",
    "faq.q3.a":
      "All lamps in the shop are ready to use in Denmark — 230V, Danish plug. If we ship to a country with a different electrical standard, you'll need to use an adapter or have the lamp re-wired locally.",
    "faq.q4.q": "How do you pack the lamps?",
    "faq.q4.a":
      "Each lamp is packed in bubble wrap, newspaper and a sturdy box — with extra padding if the lamp is particularly delicate. We've shipped worldwide without issues.",
    "faq.q5.q": "Can I see the lamp first?",
    "faq.q5.a":
      "Absolutely. We have a small showroom in Frederiksberg and welcome visits by appointment. Book a viewing and we'll find a time together.",
    "faq.q6.q": "What payment methods do you accept?",
    "faq.q6.a":
      "MobilePay, Klarna, Visa, Mastercard and Apple Pay. Everything goes through a secure payment page.",
    "faq.q7.q": "Why are some measurements missing?",
    "faq.q7.a":
      "We try to include the most important dimensions — diameter, height, fitting type. If you need a specific measurement, just ask and we'll measure right away.",
    "faq.q8.q": "Do you ship outside the EU?",
    "faq.q8.a":
      "Yes. We ship to the UK, USA, Australia, Asia and more. Note that customs duties and local VAT may apply in your country.",
    "faq.q9.q": "Can you find a specific lamp for me?",
    "faq.q9.a":
      "Often yes. We have good contacts with distributors in Italy. Use the 'Find me a lamp' form — it's no obligation.",
    "faq.q10.q": "What if the lamp arrives damaged?",
    "faq.q10.a":
      "Write to us with photos as soon as possible. We insure all parcels and we'll find a solution — repair, partial refund or full refund.",

    // Terms page
    "terms.eyebrow": "Terms of sale",
    "terms.titleA": "Terms",
    "terms.titleB": "of sale.",
    "terms.intro":
      "These terms apply to purchases on farveladeland.com. We follow Danish consumer protection law.",
    "terms.seller.title": "Seller",
    "terms.seller.body":
      "Farveladeland\nMunkensvej 4, 2.tv\n2000 Frederiksberg\nDenmark\nbutik@farveladeland.com",
    "terms.prices.title": "Prices",
    "terms.prices.body":
      "All prices are in Danish kroner and include 25% VAT. Shipping is calculated separately at checkout, based on the lamp's size and the destination.",
    "terms.payment.title": "Payment",
    "terms.payment.body":
      "We accept MobilePay, Klarna, Visa, Mastercard and Apple Pay. Payment is taken when the order is dispatched.",
    "terms.delivery.title": "Delivery",
    "terms.delivery.body":
      "We ship every Monday from Frederiksberg. Within Denmark, expect your parcel 1–3 business days after dispatch. See our shipping page for details.",
    "terms.withdrawal.title": "Right of withdrawal",
    "terms.withdrawal.body":
      "You have 14 days from receipt to change your mind. See our returns page for details.",
    "terms.complaint.title": "Defects",
    "terms.complaint.body":
      "You have a 24-month warranty under Danish consumer law. Defects should be reported within reasonable time after they are discovered.",
    "terms.disputes.title": "Dispute resolution",
    "terms.disputes.body":
      "If you're unhappy and we can't find a solution together, you can file a complaint with the Danish Consumer Complaints Board (forbrug.dk) or via the EU online dispute resolution platform: https://ec.europa.eu/consumers/odr",
    "terms.law.title": "Governing law",
    "terms.law.body": "Purchases at Farveladeland are governed by Danish law.",

    // Privacy
    "privacy.eyebrow": "Privacy",
    "privacy.titleA": "Privacy",
    "privacy.titleB": "policy.",
    "privacy.intro":
      "We treat your personal data with respect and only use it for the purposes you have consented to. Here's what we collect, why, and what you can do about it.",
    "privacy.collect.title": "What we collect",
    "privacy.collect.body":
      "When you shop with us: name, delivery address, email, phone, order history. When you write to us: your email and message. When you visit the site: anonymous statistics about pages visited (no personally identifiable data).",
    "privacy.why.title": "Why",
    "privacy.why.body":
      "We use your data to process your order, ship your parcel, reply to your questions and comply with bookkeeping law. We don't send newsletters without your consent.",
    "privacy.share.title": "Who we share with",
    "privacy.share.body":
      "With shipping providers (GLS) to deliver your parcel. With payment providers to receive payment. With our hosting and e-commerce providers (Shopify) to run the shop. We never sell your data.",
    "privacy.retention.title": "Retention",
    "privacy.retention.body":
      "Order details are kept for 5 years to comply with bookkeeping law. Customer enquiries are deleted after 2 years.",
    "privacy.rights.title": "Your rights",
    "privacy.rights.body":
      "You can request access to, correction of or deletion of your data at any time. Write to butik@farveladeland.com. You can also file a complaint with the Danish Data Protection Agency (datatilsynet.dk).",
    "privacy.cookies.title": "Cookies",
    "privacy.cookies.body":
      "Necessary cookies and local storage make the shop work — including your basket, checkout, wishlist and your cookie choice. Only with your consent do we also use: preferences (viewed products and recent searches, stored on your device), statistics (Google Analytics and an anonymous view counter; Google may process data in the USA) and marketing (sharing visits and purchases with advertising partners such as Google and via Shopify). None of this loads before you consent. Your choice is kept for 12 months, and you can change or withdraw it at any time under “Cookie settings” at the bottom of the page.",

    // Newsletter
    "newsletter.eyebrow": "Newsletter",
    "newsletter.title": "Be the first to see new finds",
    "newsletter.body":
      "We send a short email when new Murano lamps and flea-market finds land in the shop. No spam — unsubscribe anytime.",
    "newsletter.placeholder": "Your email",
    "newsletter.cta": "Sign up",
    "newsletter.consent": "We only use your email for the newsletter — nothing else.",

    // Cookie banner
    "cookie.aria": "Cookie consent",
    "cookie.eyebrow": "Cookies",
    "cookie.title": "This site uses cookies",
    "cookie.body":
      "Necessary cookies make the shop work. With your consent we also use cookies to remember your preferences, measure traffic and measure marketing. You can change your choice any time under “Cookie settings” at the bottom of the page.",
    "cookie.readMore": "Read more",
    "cookie.accept": "Accept all",
    "cookie.reject": "Reject all",
    "cookie.customize": "Customise",
    "cookie.save": "Save choices",
    "cookie.alwaysOn": "Always on",
    "cookie.close": "Close",
    "cookie.settings": "Cookie settings",
    "cookie.necessary.title": "Necessary",
    "cookie.necessary.body":
      "Make the shop work: cart, checkout, wishlist and your cookie choice. Can't be turned off.",
    "cookie.preferences.title": "Preferences",
    "cookie.preferences.body":
      "Remember products you've viewed and your recent searches so you can find them again. Stored only on your device.",
    "cookie.statistics.title": "Statistics",
    "cookie.statistics.body":
      "Google Analytics and an anonymous view counter show us which pages and products are seen. Google may process data in the USA.",
    "cookie.marketing.title": "Marketing",
    "cookie.marketing.body":
      "Shares your visits and purchases with our advertising partners (including Google and via Shopify) so we can measure and target ads.",

    // Summer sale popup
    "sale.aria": "Summer Sale",
    "sale.eyebrow": "Just in",
    "sale.title": "Summer Sale",
    "sale.body": "Check out our new Summer Sale collection — selected vintage finds with 27% off, applied automatically at checkout.",
    "sale.cta": "Shop Summer Sale",
    "sale.close": "Close",
    "sale.pill": "Summer Sale",
    "sale.pillAria": "Open Summer Sale",
    "sale.badge": "−27%",

    "newsletter.success": "Thanks — you're on the list.",
    "newsletter.error": "Something went wrong. Please try again.",
    "newsletter.invalid": "Please double-check your email.",

    // Exit intent
    "exit.eyebrow": "Before you go",
    "exit.title": "Want first dibs on the new finds?",
    "exit.body":
      "Our Murano lamps often sell the same day. Sign up and we'll email you when new pieces land.",
    "exit.dismiss": "No thanks",

    // Product CRO
    "product.unique": "One of a kind — vintage",
    "product.delivery": "Usually delivered in 3–7 business days in DK",
    "product.expressBuy": "Buy now",
    "product.lastOne": "Last one",
  },
} as const;

export type DictKey = keyof typeof dict.da;

export function t(locale: Locale, key: DictKey, vars?: Record<string, string | number>): string {
  const d = dict[locale] ?? dict.da;
  let s: string = (d as Record<string, string>)[key] ?? (dict.da as Record<string, string>)[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return s;
}

// Pluralization helper using *_one / *_other
export function tn(
  locale: Locale,
  baseKey: string,
  n: number,
  vars?: Record<string, string | number>,
): string {
  const suffix = n === 1 ? "_one" : "_other";
  return t(locale, `${baseKey}${suffix}` as DictKey, { n, ...(vars ?? {}) });
}

export function useT() {
  const locale = useLocale();
  return {
    locale,
    t: (key: DictKey, vars?: Record<string, string | number>) => t(locale, key, vars),
    tn: (baseKey: string, n: number, vars?: Record<string, string | number>) =>
      tn(locale, baseKey, n, vars),
  };
}
